import { LookupValue } from './lookup.models';

// Capacity and Availability Management interfaces

// Well-known capacity status codes for logic comparisons
// These match the codes in the RESOURCE_CAPACITY_STATUS lookup type
export const CAPACITY_STATUS_CODES = {
  UNKNOWN: 'UNKNOWN',
  BELOW_CAPACITY: 'BELOW_CAPACITY',
  AT_CAPACITY: 'AT_CAPACITY',
  OVER_CAPACITY: 'OVER_CAPACITY',
  NOT_AVAILABLE: 'NOT_AVAILABLE',
  MIXED: 'MIXED',
} as const;

export interface WeeklyCapacity {
  id?: number;
  employeeId: string;
  weekStartDate: string; // ISO date string (Monday of the week)
  weekEndDate: string; // ISO date string (Sunday of the week)
  capacityStatus?: LookupValue | null; // Full lookup value from API
  notes?: string;
  /**
   * Expected billable hours the resource reported for this period, or null for none.
   *
   * A period TOTAL, matching `ResourceCapacityDto.expectedHours`, not a per-week figure. Null and
   * 0 are different: null means nothing was entered, 0 means the resource reported zero hours.
   */
  expectedHours?: number | null;
  createdAt?: string;
  updatedAt?: string;
  rawData?: ResourceCapacityDto; // Store raw backend data for mixed status details
}

export interface CreateWeeklyCapacityRequest {
  weekStartDate: string;
  weekEndDate: string;
  capacityStatusCode: string; // Lookup code (e.g., "AT_CAPACITY")
  notes?: string;
}

export interface UpdateWeeklyCapacityRequest {
  capacityStatusCode?: string; // Lookup code
  notes?: string;
}

export interface CreateResourceCapacityRequest {
  employeeId: string;
  date: string; // ISO date string
  capacityStatusCode: string; // Capacity status code (e.g., "AT_CAPACITY", "BELOW_CAPACITY")
  comments?: string;
  /**
   * Expected billable hours for the whole period, 0-999.
   *
   * This is a PERIOD TOTAL: the API divides it across the interval's weeks on write, so the
   * interval reads back the value supplied here. Do not send a per-week figure — the override
   * request's `expectedHoursPerWeek` is the per-week one, and the two are deliberately named
   * apart because the same number means different things on the two paths.
   *
   * Required only when the resource's office and practice area are configured to require it.
   *
   * Omitting it PRESERVES whatever the interval already holds — send `clearExpectedHours` to
   * remove a stored value.
   */
  expectedHours?: number;

  /**
   * Removes the stored expected hours for every week in the interval.
   *
   * Absent and cleared are different requests: an ordinary status change omits `expectedHours` and
   * must not erase what the resource reported, so emptying the field has to say so explicitly.
   * Rejected by the API alongside a value, and rejected where hours are mandatory.
   */
  clearExpectedHours?: boolean;
}

export interface WeekPeriod {
  startDate: Date;
  endDate: Date;
  weekNumber: number;
  year: number;
  displayText: string; // e.g., "Week 42: Oct 13 - Oct 19, 2024"
}

// Matches the backend ResourceCapacityOverrideDto
export interface ResourceCapacityOverride {
  id: number;
  employeeId: string;
  capacityStatusValueId: number | null;
  capacityStatusCode: string | null;
  capacityStatusName: string | null;
  capacityOverrideStartDate: string; // ISO date string
  capacityOverrideEndDate: string | null; // null = still open
  expectedHoursPerWeek: number | null;
}

export interface UpsertResourceCapacityOverrideRequest {
  employeeId: string;
  capacityStatusCode: string | null;
  /**
   * Expected billable hours PER WEEK, 0-999, applied to every week this override covers.
   *
   * Stored as given rather than divided, because the read path already scales an override by
   * the interval length. Named apart from the create request's `expectedHours`, which is a
   * period total divided across the interval's weeks — with a 2-week interval, 40 here reads
   * back as 80, while 40 there reads back as 40. Never share a mapper between the two.
   *
   * Never required when ending an override.
   */
  expectedHoursPerWeek?: number | null;
}

// UI state management interfaces
export interface CapacityFormData {
  capacityStatusCode: string; // Lookup code
  notes: string;
}

// Helper interface for capacity status options in dropdowns
export interface CapacityStatusOption {
  value: string; // Lookup code (e.g., "AT_CAPACITY")
  label: string; // Display label
  description?: string;
}

// API Response Models - these match the backend DTOs
export interface ResourceCapacityWeekDto {
  resourceCapacitiesId: number | null;
  employeeId: string;
  capacityStatus: LookupValue | null; // Full lookup value with code, name, etc.
  comments: string | null;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  createdAt: string | null;
  createdBy: number | null;
  updatedAt: string | null;
  updatedBy: number | null;
  /** Expected billable hours reported for this single week (null if none). */
  expectedHours: number | null;
}

export interface ResourceCapacityDto {
  resourceCapacitiesId: number | null;
  employeeId: string;
  capacityStatus: LookupValue | null; // Full lookup value: UNKNOWN, BELOW_CAPACITY, AT_CAPACITY, OVER_CAPACITY, NOT_AVAILABLE, MIXED
  comments: string | null;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  createdAt: string | null;
  createdBy: number | null;
  updatedAt: string | null;
  updatedBy: number | null;
  /**
   * Expected billable hours for the whole interval (null if none reported).
   *
   * An interval TOTAL, not per week — it is the sum across `weeklyCapacities`, resolved
   * server-side and rounded once.
   */
  expectedHours: number | null;
  weeklyCapacities: ResourceCapacityWeekDto[];
}

export interface ResourceCapacitiesResponseDto {
  employeeId: string;
  capacityInterval: number;
  intervalStartDate: string | null; // ISO date string, null when no interval is configured
  intervalWeekOffset: number;
  currentCapacityStatusOverride: LookupValue | null; // Currently active capacity status override (null if none)
  /**
   * Whether this resource must supply expected hours when their capacity is saved, because their
   * primary office and practice area are configured to require it.
   *
   * Advisory: the server enforces the rule on every write whatever this said, so treat it as "mark
   * the field required", not as permission to skip validation. False while the
   * `expected-period-hours` flag is off, and false on the batch read the Resources Directory uses —
   * that path deliberately does not resolve it.
   */
  expectedHoursRequired: boolean;
  resourceCapacities: ResourceCapacityDto[];
}
